import isFunction from 'lodash/isFunction'
import pick from 'lodash/pick'

import {
  updateCurrentDate,
  updateCurrentNumMembers,
  updateCurrentOwnerRole,
  updateCurrentType,
} from '../../../actions/events'
import {fsa, fsaError, fsaPayload, fsaPayloadByKeys} from '../../../actions/helpers'
import * as api from '../../../store/api'
import {
  Madlibs,
  OnboardingSteps,
  OwnerFunnel,
  RegisteredOwnerFunnel,
  SurveyNames,
  SurveyQuestions,
} from '../../../store/enum'
import * as onboarding from './store'
import * as events from '../../../store/events'
import * as user from '../../../store/user'
import * as track from '../../../track/track'
import * as URLS from '../../../urls'
import {postData} from '../../../utils'
import SERVICES from '../../../config/services'

// actions

export const CHANGE_STEP = 'change the onboarding step'
export const changeStep = fsaPayloadByKeys(CHANGE_STEP, ['direction', 'step'])

export const CLEAR_ON_SUCCESS = 'clear onSuccess callback'
export const clearOnSuccess = fsa(CLEAR_ON_SUCCESS)

export const CLOSE_ONBOARDING = 'close the onboarding modal'
export const closeOnboarding = fsa(CLOSE_ONBOARDING)

// this is to be fired as a side-effect from the component itself, because the
//  modal is becomes visible through a mixture of state and screen size
export const CLOSED_MADLIBS_ONBOARDING = 'closed the madlibs onboarding modal'
export const closedMadlibsOnboarding = fsa(CLOSED_MADLIBS_ONBOARDING)

export const LOGIN_VIA_ONBOARDING = 'login via the onboarding modal'
export const loginViaOnboarding = fsaPayload(LOGIN_VIA_ONBOARDING)

export const COMPLETE_ONBOARDING = 'complete onboarding'
export const completeOnboarding = fsa(COMPLETE_ONBOARDING)

export const NO_DIRECTION = 'NO_DIRECTION'
export const setNoDirection = fsa(NO_DIRECTION)

export const ONBOARDING_ATTRIBUTION = 'ONBOARDING_ATTRIBUTION'
export const onboardingAttribution = fsaPayload(ONBOARDING_ATTRIBUTION)

export const OPEN_ONBOARDING = 'OPEN_ONBOARDING'
export const openOnboardingModal = fsaPayload(OPEN_ONBOARDING)

// this is to be fired as a side-effect from the component itself, because the
//  modal is becomes hidden by a change in state and screen size
export const OPENED_MADLIBS_ONBOARDING = 'opened the madlibs onboarding modal'
export const openedMadlibsOnboarding = fsa(OPENED_MADLIBS_ONBOARDING)

export const REGISTER_VIA_ONBOARDING = 'register via the onboarding modal'
export const registerViaOnboarding = fsaPayload(REGISTER_VIA_ONBOARDING)

export const RESET_PASSWORD_REQUEST_VIA_ONBOARDING =
  'reset password request via the onboarding modal'
export const resetPasswordRequestViaOnboarding = fsaPayload(RESET_PASSWORD_REQUEST_VIA_ONBOARDING)

export const SHOW_ONBOARDING_PASSWORD_RESET_REQUEST_FORM =
  'show the onboarding password reset request form'

export const showOnboardingPasswordResetRequestForm = fsaPayload(
  SHOW_ONBOARDING_PASSWORD_RESET_REQUEST_FORM,
)

export const SHOW_ONBOARDING_PASSWORD_RESET_REQUEST_SUCCESS =
  'show the onboarding password reset success message'
export const showOnboardingPasswordResetRequestSuccess = fsa(
  SHOW_ONBOARDING_PASSWORD_RESET_REQUEST_SUCCESS,
)

export const SUBMIT_ATTRIBUTION_SURVEY_PENDING = 'submit attribution survey pending'
export const submitAttributionSurveyPending = fsa(SUBMIT_ATTRIBUTION_SURVEY_PENDING)

export const SUBMIT_ATTRIBUTION_SURVEY_SUCCESSFUL = 'submit attribution survey successful'
export const submitAttributionSurveySuccessful = fsa(SUBMIT_ATTRIBUTION_SURVEY_SUCCESSFUL)

export const SUBMIT_ATTRIBUTION_SURVEY_FAILURE = 'submit attribution survey failed'
export const submitAttributionSurveyFailure = fsaError(SUBMIT_ATTRIBUTION_SURVEY_FAILURE)

export const SET_REGISTERED_FLAG = 'SET_REGISTERED_FLAG'
export const setRegisteredFlag = fsaPayload(SET_REGISTERED_FLAG)

export const SET_STEP = 'SET_STEP'
export const setStep = fsaPayload(SET_STEP)

export const GO_FORWARDS = 'onboarding/go forwards'
export const goForwards = fsa(GO_FORWARDS)

export const GO_BACKWARDS = 'onboarding/go backwards'
export const goBackwards = fsa(GO_BACKWARDS)

export const setStepLogin = () => setStep(OnboardingSteps.login)
export const setStepRegistration = () => setStep(OnboardingSteps.registration)

export const TOGGLE_MADLIBS_EDITING_ID = 'toggle which field madlibs is editing'
export const _toggleMadlibsEditingId = fsaPayloadByKeys(TOGGLE_MADLIBS_EDITING_ID, ['id'])
// to avoid polluting the log, don't dispatch this action if there would be
//  no effect; toggleMadlibsEditingId acts as a gate to _toggleMadlibsEditingId
export const toggleMadlibsEditingId = (id) => (dispatch, getState) =>
  (id || onboarding.madlibsIsEditing(getState())) && dispatch(_toggleMadlibsEditingId(id))

// thunks

export function openOnboarding(onSuccess) {
  return (dispatch, getState) => {
    const state = getState()
    const loggedIn = user.isAuthenticated(state)
    dispatch(setRegisteredFlag(loggedIn))
    dispatch(openOnboardingModal(onSuccess))
  }
}

// starts onboarding at correct step and invokes given success function when complete
export function initiateOnboarding(callback) {
  return (dispatch, getState) => {
    const state = getState()
    const authenticated = user.isAuthenticated(state)
    const hasEventDetails = events.currentEventHasEventDetails(state)
    if (hasEventDetails) {
      // if user is authenticated and has event details, invoke success function (if given)
      if (authenticated) {
        if (isFunction(callback)) callback()
        // if user is not authenticated open onboarding at the registration step
      } else {
        dispatch(setStepRegistration())
        dispatch(openOnboarding(callback))
      }
      // if there are no event details, start onboarding from the beginning
    } else {
      // if authenticated, start them in registered flow otherwise start in normal flow
      if (authenticated) {
        dispatch(setStep(RegisteredOwnerFunnel[0]))
      } else {
        dispatch(setStep(OwnerFunnel[0]))
      }
      dispatch(openOnboarding(callback))
    }
  }
}

export function submitAttributionSurvey() {
  return (dispatch, getState) => {
    const state = getState()
    const onboardingFunnel = onboarding.getFunnel(state)
    const onboardingStep = onboarding.step(state)
    const isLastStepInOnboardingFunnel =
      onboardingFunnel.indexOf(onboardingStep) === onboardingFunnel.length - 1

    dispatch(submitAttributionSurveyPending())

    // if attributionAnswer is nil, navigate to the suits and tuxedos
    const attributionAnswer = onboarding.attributionAnswer(state)
    const submitSurveyAction = !isLastStepInOnboardingFunnel ? goForwards : closeOnboarding

    if (!attributionAnswer) {
      dispatch(submitSurveyAction())
      return dispatch(submitAttributionSurveyFailure())
    }

    // send survey answer choice
    const payload = {
      answer: attributionAnswer,
      survey_name: SurveyNames.onboardingAttribution,
      question: SurveyQuestions.attribution,
    }
    const success = () => {
      track.onboardingAttributionSurveySubmitted(attributionAnswer)
      dispatch(submitAttributionSurveySuccessful())
      dispatch(submitSurveyAction())
    }
    const error = () => {
      dispatch(submitAttributionSurveyFailure())
      dispatch(submitSurveyAction())
    }
    return postData(`${SERVICES.TBT_API_URL}${URLS.SURVEY}`, payload, success, error)
  }
}

export const clickedOnboardingHeaderRegistration = () => (dispatch, getState) => {
  const state = getState()

  if (api.participant.isSuccess(state)) {
    // we're handling a participant invite--we just want to switch them to the registration form
    dispatch(setStepRegistration())
  } else {
    // let the user start onboarding at the appropriate step
    dispatch(initiateOnboarding())
  }
}

// -- Madlibs

export const madlibsGetStarted = () => (dispatch, getState) => {
  const event = pick(events.current(getState()), ['date', 'numMembers', 'ownerRole', 'type'])

  // pass in the event data we've collected
  track.clickedMadlibsGetStarted(event)
  // start onboarding at registration step
  dispatch(initiateOnboarding())
}

const getUpdateAction = (id, value) => {
  switch (id) {
    case Madlibs.eventDate:
      return updateCurrentDate(value)
    case Madlibs.eventRole:
      return updateCurrentOwnerRole(value)
    case Madlibs.eventType:
      return updateCurrentType(value)
    case Madlibs.numPeople:
      return updateCurrentNumMembers(value)
    default:
      return undefined
  }
}

export const selectMadlibsOption = (id, value, context) => (dispatch) => {
  // report the id, value, and context (where it was submitted)
  track.selectedMadlibsOption(id, value, context)
  // then update that piece of event data in the store
  dispatch(getUpdateAction(id, value))
  // conclude editing (close open dropdown/modal)
  dispatch(toggleMadlibsEditingId())
}
